import { logError, rawFetch } from './errorLog';

export class WebhookError extends Error {
  constructor(message, { url, serverMessage, ...context } = {}) {
    super(message);
    this.name = 'WebhookError';
    this.serverMessage = serverMessage;
    // Picked up by logError, so every report carries the failing webhook.
    this.details = { webhook: url, serverMessage, ...context };
  }
}

// Limits for fetchWithTimeout. Saving or opening a document is a quick database
// call; a Word export can take a while on a busy server.
export const DOCUMENT_TIMEOUT_MS = 60 * 1000;
export const EXPORT_TIMEOUT_MS = 3 * 60 * 1000;

// fetch that gives up when no reply has come after `ms`, so a stuck server ends
// in an error the page can show instead of a spinner that never stops. The
// error's name is 'TimeoutError'. Only for calls with a known upper limit (saves,
// loads, file exports); generation calls can take several minutes and have none.
export async function fetchWithTimeout(url, init = {}, ms) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } catch (err) {
    if (!controller.signal.aborted) throw err;
    const timeout = new Error(`No reply after ${Math.round(ms / 1000)} s`);
    timeout.name = 'TimeoutError';
    throw timeout;
  } finally {
    clearTimeout(timer);
  }
}

// POST to an n8n webhook and return the parsed reply (null for an empty body).
// Throws a WebhookError, already reported, when the request fails, the reply
// isn't JSON, or n8n answers 200 but says it failed ({ error } / { success: false }).
// `requireSuccess` also rejects replies that lack `success: true`.
// `allowFailureReply` returns a 200 reply that says it failed, for callers that read why.
// Pass `json` for a JSON body, or `body` for FormData.
export async function postWebhook(url, { json, requireSuccess = false, allowFailureReply = false, ...init } = {}) {
  const fail = (message, extra) => {
    const err = new WebhookError(message, { url, ...extra });
    logError('webhook', err);
    throw err;
  };

  let response;
  try {
    response = await rawFetch(url, {
      method: 'POST',
      ...init,
      ...(json !== undefined && {
        headers: { 'Content-Type': 'application/json', ...init.headers },
        body: JSON.stringify(json)
      })
    });
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    fail('network_error', { serverMessage: err?.message });
  }

  const text = await response.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      fail(response.ok ? 'invalid_response' : `HTTP ${response.status}`, { status: response.status });
    }
  }

  // n8n may wrap a single item in an array.
  const first = Array.isArray(data) ? data[0] : data;
  const serverMessage = first?.message || (typeof first?.error === 'string' ? first.error : undefined);
  // errorCode and executionId point at the n8n execution that failed.
  const context = { status: response.status, serverMessage, errorCode: first?.errorCode, executionId: first?.executionId };
  if (!response.ok) fail(`HTTP ${response.status}`, context);
  if (!allowFailureReply && (first?.error || first?.success === false || (requireSuccess && first?.success !== true))) {
    fail(serverMessage || 'webhook_reported_failure', context);
  }
  return data;
}

// For pages that show the failure text to the teacher. Same as postWebhook, but
// the thrown WebhookError's `message` is safe to display: the workflow's own
// `message` when it sent one, `networkMessage` when nothing came back, and
// `fallback` otherwise. An empty reply also counts as a failure.
export async function postForDisplay(url, { fallback, networkMessage = fallback, ...options }) {
  try {
    const data = await postWebhook(url, options);
    if (data) return data;
    const err = new WebhookError('empty_response', { url });
    logError('webhook', err);
    throw err;
  } catch (err) {
    if (err instanceof WebhookError) {
      // Already reported with its technical message; from here on it is only shown.
      err.message = err.message === 'network_error' ? networkMessage : err.serverMessage || fallback;
    }
    throw err;
  }
}
